// src/components/PWAUpdatePrompt.js
// Prompt pour mettre à jour la PWA quand une nouvelle version est disponible
import React, { useEffect, useRef, useState } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { captureException } from '../utils/sentry'
import './PWAUpdatePrompt.css'

const PWAUpdatePrompt = () => {
  // Ref vers le timer du poll de mise à jour PWA. Stocké en ref pour
  // (a) survivre aux re-renders sans relancer un nouvel intervalle,
  // (b) permettre un cleanup propre au démontage, (c) garder une trace
  // unique si `onRegistered` est rappelé (StrictMode dev, retry plugin).
  const updateIntervalRef = useRef(null)

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('✅ Service Worker enregistré:', r)

      if (!r) return

      // r.update() peut rejeter (Promise) ou throw (sync) avec un TypeError
      // "Cannot update a null/nonexistent service worker registration" — SW
      // devenu orphelin (rename de slug, fichier SW 404, unregister concurrent).
      // Sans catch, l'unhandledrejection bubble jusqu'à l'ErrorBoundary global
      // et l'écran flippe sur "Un problème est survenu".
      // On remonte en `warning` (pas error) pour avoir la télémétrie sans
      // saturer le quota — c'est un signal qu'on veut mesurer, pas ignorer.
      const reportSwFailure = (err, phase) => {
        console.warn(`⚠️ [PWA] update() ${phase} :`, err?.message || err)
        captureException(err instanceof Error ? err : new Error(String(err)), {
          tags: { source: 'pwa-update', phase },
          level: 'warning',
        })
      }

      const safeUpdate = () => {
        try {
          const p = r.update()
          if (p && typeof p.catch === 'function') {
            p.catch((err) => reportSwFailure(err, 'async'))
          }
        } catch (err) {
          reportSwFailure(err, 'sync')
        }
      }

      // Vérifier immédiatement au démarrage
      safeUpdate()

      // Garde-fou anti-accumulation : si onRegistered est rappelé (re-mount
      // StrictMode, retry vite-plugin-pwa), on dégage l'intervalle existant
      // avant d'en armer un nouveau, sinon on accumule plusieurs polls 5min.
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current)
      }
      updateIntervalRef.current = setInterval(() => {
        console.log('🔍 Vérification des mises à jour PWA...')
        safeUpdate()
      }, 5 * 60 * 1000) // 5 minutes
    },
    onRegisterError(error) {
      console.error('❌ Erreur d\'enregistrement du SW:', error)
    },
    onNeedRefresh() {
      console.log('🔄 Nouvelle version PWA détectée !')
    },
  })

  // Cleanup unique au démontage du composant — libère le timer du poll
  // de mise à jour PWA. Dépendances vides : un seul cleanup à la fin de vie.
  useEffect(() => {
    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current)
        updateIntervalRef.current = null
      }
    }
  }, [])

  const close = () => {
    setOfflineReady(false)
    setNeedRefresh(false)
  }

  const handleUpdate = () => {
    console.log('⬇️ Mise à jour de la PWA...')
    updateServiceWorker(true)
    // Le rechargement se fera automatiquement avec skipWaiting: true
  }

  // Auto-fermer le message "App prête hors ligne" après 3 secondes
  useEffect(() => {
    if (offlineReady) {
      const timer = setTimeout(() => {
        setOfflineReady(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [offlineReady, setOfflineReady])

  if (!offlineReady && !needRefresh) return null

  return (
    <div className="pwa-update-prompt">
      <div className="pwa-update-card">
        {offlineReady ? (
          <>
            <div className="pwa-update-icon success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <div className="pwa-update-content">
              <h3>Application prête !</h3>
              <p>Terrilink fonctionne maintenant hors ligne</p>
            </div>
            <button className="pwa-update-btn secondary" onClick={close}>
              OK
            </button>
          </>
        ) : (
          <>
            <div className="pwa-update-icon info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
              </svg>
            </div>
            <div className="pwa-update-content">
              <h3>Nouvelle version disponible</h3>
              <p>Rechargez pour profiter des dernières améliorations</p>
            </div>
            <div className="pwa-update-actions">
              <button className="pwa-update-btn secondary" onClick={close}>
                Plus tard
              </button>
              <button className="pwa-update-btn primary" onClick={handleUpdate}>
                Mettre à jour
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default PWAUpdatePrompt
